import { t } from "@excalidraw/excalidraw/i18n";
import { useEffect, useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { atom, useAtom } from "../app-jotai";

import "./GridSettingsDialog.scss";

// Opened from the main menu's single "Cuadrícula" button.
export const gridSettingsOpenAtom = atom(false);

type GridStyle = "grid" | "lines" | "dots";

const STYLES: { value: GridStyle; labelKey: string }[] = [
  { value: "grid", labelKey: "labels.gridStyle_grid" },
  { value: "lines", labelKey: "labels.gridStyle_lines" },
  { value: "dots", labelKey: "labels.gridStyle_dots" },
];

export const GridSettingsDialog = ({
  excalidrawAPI,
}: {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}) => {
  const [open, setOpen] = useAtom(gridSettingsOpenAtom);
  const [enabled, setEnabled] = useState(false);
  const [style, setStyle] = useState<GridStyle>("grid");
  const [color, setColor] = useState("#000000");
  const [opacity, setOpacity] = useState(6);
  const [dark, setDark] = useState(false);

  // Initialise from the current canvas state every time the dialog opens.
  useEffect(() => {
    if (!open || !excalidrawAPI) {
      return;
    }
    const s = excalidrawAPI.getAppState();
    setEnabled(s.gridModeEnabled);
    setStyle((s.gridStyle as GridStyle) ?? "grid");
    setColor(s.gridColor ?? "#000000");
    setOpacity(typeof s.gridOpacity === "number" ? s.gridOpacity : 6);
    setDark(s.theme === "dark");
  }, [open, excalidrawAPI]);

  if (!open) {
    return null;
  }

  // Push a change to the canvas (and keep local state in sync).
  const apply = (patch: {
    gridModeEnabled?: boolean;
    gridStyle?: GridStyle;
    gridColor?: string;
    gridOpacity?: number;
  }) => {
    excalidrawAPI?.updateScene({ appState: patch as never });
  };

  const close = () => setOpen(false);

  return (
    <div
      className={`grid-settings__overlay${dark ? " grid-settings--dark" : ""}`}
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) {
          close();
        }
      }}
    >
      <div className="grid-settings" role="dialog" aria-label={t("grid.title")}>
        <div className="grid-settings__header">
          <span className="grid-settings__title">{t("grid.title")}</span>
          <button
            type="button"
            className="grid-settings__close"
            aria-label={t("grid.done")}
            onClick={close}
          >
            ✕
          </button>
        </div>

        {/* Show / hide */}
        <label className="grid-settings__row grid-settings__toggle">
          <span>{t("grid.show")}</span>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => {
              setEnabled(e.target.checked);
              apply({ gridModeEnabled: e.target.checked });
            }}
          />
        </label>

        {/* Style */}
        <div className="grid-settings__row grid-settings__col">
          <span className="grid-settings__label">{t("grid.styleLabel")}</span>
          <div className="grid-settings__styles">
            {STYLES.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`grid-settings__style${
                  style === s.value ? " is-selected" : ""
                }`}
                disabled={!enabled}
                onClick={() => {
                  setStyle(s.value);
                  apply({ gridStyle: s.value, gridModeEnabled: true });
                  setEnabled(true);
                }}
              >
                {t(s.labelKey as never)}
              </button>
            ))}
          </div>
        </div>

        {/* Color */}
        <label className="grid-settings__row">
          <span className="grid-settings__label">{t("grid.color")}</span>
          <span className="grid-settings__color">
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : "#000000"}
              disabled={!enabled}
              onChange={(e) => {
                setColor(e.target.value);
                apply({ gridColor: e.target.value });
              }}
            />
            <span className="grid-settings__hex">{color}</span>
          </span>
        </label>

        {/* Opacity */}
        <div className="grid-settings__row grid-settings__col">
          <span className="grid-settings__label">
            {t("grid.opacity")} — {opacity}%
          </span>
          <input
            type="range"
            min={0}
            max={100}
            value={opacity}
            disabled={!enabled}
            onChange={(e) => {
              const v = Number(e.target.value);
              setOpacity(v);
              apply({ gridOpacity: v });
            }}
          />
        </div>

        <div className="grid-settings__actions">
          <button
            type="button"
            className="grid-settings__btn grid-settings__btn--primary"
            onClick={close}
          >
            {t("grid.done")}
          </button>
        </div>
      </div>
    </div>
  );
};
